import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ExportTools = ({ selectedDataset, queryResults }) => {
  const [exportConfig, setExportConfig] = useState({
    format: 'csv',
    includeMetadata: true,
    includeCitations: true,
    anonymizationLevel: 'standard',
    compressionType: 'zip',
    splitLargeFiles: true,
    maxFileSize: '100mb'
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportHistory, setExportHistory] = useState([]);

  const formatOptions = [
    { value: 'csv', label: 'CSV (Comma Separated Values)', description: 'Standard spreadsheet format' },
    { value: 'json', label: 'JSON (JavaScript Object Notation)', description: 'Structured data format' },
    { value: 'parquet', label: 'Apache Parquet', description: 'Columnar storage format' },
    { value: 'xlsx', label: 'Excel Spreadsheet', description: 'Microsoft Excel format' },
    { value: 'rdf', label: 'RDF/Turtle', description: 'Semantic web format' },
    { value: 'spss', label: 'SPSS Data File', description: 'Statistical analysis format' },
    { value: 'r', label: 'R Data Frame', description: 'R statistical computing format' },
    { value: 'sql', label: 'SQL Dump', description: 'Database export format' }
  ];

  const anonymizationOptions = [
    { value: 'minimal', label: 'Minimal', description: 'Basic identifier removal' },
    { value: 'standard', label: 'Standard', description: 'GDPR-compliant anonymization' },
    { value: 'enhanced', label: 'Enhanced', description: 'Advanced privacy protection' },
    { value: 'maximum', label: 'Maximum', description: 'Full anonymization with aggregation' }
  ];

  const compressionOptions = [
    { value: 'none', label: 'No Compression' },
    { value: 'zip', label: 'ZIP Archive' },
    { value: 'gzip', label: 'GZIP Compression' },
    { value: 'bzip2', label: 'BZIP2 Compression' }
  ];

  const fileSizeOptions = [
    { value: '50mb', label: '50 MB per file' },
    { value: '100mb', label: '100 MB per file' },
    { value: '250mb', label: '250 MB per file' },
    { value: '500mb', label: '500 MB per file' },
    { value: '1gb', label: '1 GB per file' },
    { value: 'no_limit', label: 'No size limit' }
  ];

  const mockExportHistory = [
    {
      id: 'exp_001',
      dataset: 'EU Cross-Border Narrative Analysis 2024',
      format: 'csv',
      size: '2.3 GB',
      records: 847293,
      timestamp: '2024-09-21T09:15:00Z',
      status: 'completed',
      downloadUrl: '#',
      expiryDate: '2024-10-21T09:15:00Z'
    },
    {
      id: 'exp_002',
      dataset: 'Platform Signals Aggregation 2024',
      format: 'json',
      size: '1.8 GB',
      records: 234567,
      timestamp: '2024-09-20T14:30:00Z',
      status: 'completed',
      downloadUrl: '#',
      expiryDate: '2024-10-20T14:30:00Z'
    },
    {
      id: 'exp_003',
      dataset: 'FIMI Threat Intelligence Q3 2024',
      format: 'parquet',
      size: '890 MB',
      records: 123456,
      timestamp: '2024-09-19T11:45:00Z',
      status: 'processing',
      downloadUrl: null,
      expiryDate: null
    }
  ];

  const citationFormats = [
    {
      name: 'APA Style',
      format: `TruthGuard EU Research Team. (${new Date()?.getFullYear()}). ${selectedDataset?.title || 'Dataset Title'}. TruthGuard EU Research Data Portal. Retrieved ${new Date()?.toLocaleDateString('en-GB')} from https://research.truthguard.eu/datasets/${selectedDataset?.id || 'dataset-id'}`
    },
    {
      name: 'MLA Style',
      format: `TruthGuard EU Research Team. "${selectedDataset?.title || 'Dataset Title'}." TruthGuard EU Research Data Portal, ${new Date()?.getFullYear()}, research.truthguard.eu/datasets/${selectedDataset?.id || 'dataset-id'}.`
    },
    {
      name: 'Chicago Style',
      format: `TruthGuard EU Research Team. "${selectedDataset?.title || 'Dataset Title'}." TruthGuard EU Research Data Portal. Accessed ${new Date()?.toLocaleDateString('en-GB')}. https://research.truthguard.eu/datasets/${selectedDataset?.id || 'dataset-id'}.`
    },
    {
      name: 'IEEE Style',
      format: `TruthGuard EU Research Team, "${selectedDataset?.title || 'Dataset Title'}," TruthGuard EU Research Data Portal, ${new Date()?.getFullYear()}. [Online]. Available: https://research.truthguard.eu/datasets/${selectedDataset?.id || 'dataset-id'}. [Accessed: ${new Date()?.toLocaleDateString('en-GB')}].`
    }
  ];

  const handleExport = async () => {
    if (!selectedDataset) return;

    setIsExporting(true);
    
    // Simulate export process
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    const newExport = {
      id: `exp_${Date.now()}`,
      dataset: selectedDataset?.title,
      format: exportConfig?.format,
      size: selectedDataset?.size,
      records: selectedDataset?.records,
      timestamp: new Date()?.toISOString(),
      status: 'completed',
      downloadUrl: '#',
      expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString() // 30 days from now
    };
    
    setExportHistory(prev => [newExport, ...prev]);
    setIsExporting(false);
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success bg-success/10';
      case 'processing':
        return 'text-warning bg-warning/10';
      case 'failed':
        return 'text-destructive bg-destructive/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Export Tools</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Generate citation-ready exports with compliance controls
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Shield" size={16} className="text-success" />
            <span className="text-xs font-medium text-success">GDPR Compliant</span>
          </div>
        </div>

        {!selectedDataset && (
          <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="AlertTriangle" size={16} className="text-warning" />
              <span className="text-sm text-warning">Select a dataset to configure export options</span>
            </div>
          </div>
        )}
      </div>
      <div className="p-6 space-y-6">
        {/* Export Configuration */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground">Export Configuration</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Export Format"
              options={formatOptions}
              value={exportConfig?.format}
              onChange={(value) => setExportConfig(prev => ({ ...prev, format: value }))}
              disabled={!selectedDataset}
            />
            <Select
              label="Anonymization Level"
              options={anonymizationOptions}
              value={exportConfig?.anonymizationLevel}
              onChange={(value) => setExportConfig(prev => ({ ...prev, anonymizationLevel: value }))}
              disabled={!selectedDataset}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Compression Type"
              options={compressionOptions}
              value={exportConfig?.compressionType}
              onChange={(value) => setExportConfig(prev => ({ ...prev, compressionType: value }))}
              disabled={!selectedDataset}
            />
            <Select
              label="Maximum File Size"
              options={fileSizeOptions}
              value={exportConfig?.maxFileSize}
              onChange={(value) => setExportConfig(prev => ({ ...prev, maxFileSize: value }))}
              disabled={!selectedDataset}
            />
          </div>

          <div className="space-y-3">
            <Checkbox
              label="Include metadata and provenance information"
              checked={exportConfig?.includeMetadata}
              onChange={(e) => setExportConfig(prev => ({ ...prev, includeMetadata: e?.target?.checked }))}
              disabled={!selectedDataset}
            />
            <Checkbox
              label="Include citation information and licensing details"
              checked={exportConfig?.includeCitations}
              onChange={(e) => setExportConfig(prev => ({ ...prev, includeCitations: e?.target?.checked }))}
              disabled={!selectedDataset}
            />
            <Checkbox
              label="Split large files automatically"
              checked={exportConfig?.splitLargeFiles}
              onChange={(e) => setExportConfig(prev => ({ ...prev, splitLargeFiles: e?.target?.checked }))}
              disabled={!selectedDataset}
            />
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div className="text-sm text-muted-foreground">
              {selectedDataset && (
                <>
                  Estimated export size: {selectedDataset?.size} • {new Intl.NumberFormat('en-EU')?.format(selectedDataset?.records)} records
                </>
              )}
            </div>
            <Button
              onClick={handleExport}
              loading={isExporting}
              disabled={!selectedDataset}
              iconName="Download"
              iconPosition="left"
            >
              {isExporting ? 'Preparing Export...' : 'Generate Export'}
            </Button>
          </div>
        </div>

        {/* Citation Generator */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground">Citation Generator</h3>
          
          {selectedDataset ? (
            <div className="space-y-4">
              {citationFormats?.map((citation, index) => (
                <div key={index} className="p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-foreground">{citation?.name}</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Copy"
                      onClick={() => copyToClipboard(citation?.format)}
                    >
                      Copy
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground font-mono leading-relaxed">
                    {citation?.format}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 bg-muted/50 rounded-lg text-center">
              <Icon name="Quote" size={32} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                Select a dataset to generate citation formats
              </p>
            </div>
          )}
        </div>

        {/* Export History */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Export History</h3>
            <Button variant="outline" size="sm" iconName="RefreshCw">
              Refresh
            </Button>
          </div>

          {mockExportHistory?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="Archive" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h4 className="text-lg font-medium text-foreground mb-2">No Export History</h4>
              <p className="text-muted-foreground">
                Your data exports will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {mockExportHistory?.map((exportItem) => (
                <div key={exportItem?.id} className="p-4 border border-border rounded-lg">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-foreground mb-1">
                        {exportItem?.dataset}
                      </h4>
                      <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                        <span>Format: {exportItem?.format?.toUpperCase()}</span>
                        <span>Size: {exportItem?.size}</span>
                        <span>Records: {new Intl.NumberFormat('en-EU')?.format(exportItem?.records)}</span>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(exportItem?.status)}`}>
                      {exportItem?.status?.toUpperCase()}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <span>Created: {formatTimestamp(exportItem?.timestamp)}</span>
                      {exportItem?.expiryDate && (
                        <span>Expires: {formatTimestamp(exportItem?.expiryDate)}</span>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      {exportItem?.status === 'completed' && (
                        <>
                          <Button variant="outline" size="sm" iconName="Download">
                            Download
                          </Button>
                          <Button variant="ghost" size="sm" iconName="Share">
                            Share
                          </Button>
                        </>
                      )}
                      {exportItem?.status === 'processing' && (
                        <Button variant="ghost" size="sm" iconName="Clock" disabled>
                          Processing...
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportTools;